import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import Navbar from '../../components/Navbar/Navbar';
import '../Catalogs/Catalogs.css';

/* ── Icons ────────────────────────────────────────── */
function IconBack() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function IconToggleOn() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="1" y="5" width="22" height="14" rx="7" ry="7" />
      <circle cx="16" cy="12" r="3" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconToggleOff() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="1" y="5" width="22" height="14" rx="7" ry="7" />
      <circle cx="8" cy="12" r="3" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* ── Success Toast ────────────────────────────────── */
function SuccessToast({ message, onDone }) {
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="usr-toast" role="status">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
      {message}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════ */
function GlobalNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRow, setConfirmRow] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [fetchError, setFetchError] = useState('');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    const { data, error } = await supabase
      .from('global_notifications')
      .select('id, display_name, description, flow_name, active')
      .order('id');
    if (error) {
      setFetchError('No se pudieron cargar las notificaciones.');
    } else {
      setNotifications(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  async function handleToggle() {
    if (!confirmRow) return;
    setActionLoading(true);
    const { error } = await supabase
      .from('global_notifications')
      .update({ active: !confirmRow.active, updated_at: new Date().toISOString() })
      .eq('id', confirmRow.id);
    setActionLoading(false);
    setConfirmRow(null);
    if (error) {
      setToast('Error al actualizar la notificación.');
    } else {
      setToast(
        confirmRow.active
          ? `"${confirmRow.display_name}" desactivada`
          : `"${confirmRow.display_name}" activada`
      );
      fetchNotifications();
    }
  }

  return (
    <div className="cat-page">
      <Navbar />

      <main className="cat-main">
        <div className="cat-crud">
          {/* ── Header ── */}
          <div className="cat-crud-header">
            <button className="cat-back-btn" onClick={() => navigate('/admin')}>
              <IconBack />
              <span>Regresar</span>
            </button>
            <h1 className="cat-crud-title">Notificaciones Globales</h1>
            {/* No "Nuevo" button — rows are managed via Supabase dashboard only */}
            <div style={{ width: '80px' }} />
          </div>

          {/* ── Body ── */}
          {loading ? (
            <div className="cat-loading">Cargando notificaciones…</div>
          ) : fetchError ? (
            <div className="cat-empty">{fetchError}</div>
          ) : notifications.length === 0 ? (
            <div className="cat-empty">No hay notificaciones registradas.</div>
          ) : (
            <div className="cat-table-wrap">
              <table className="cat-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Flujo de Power Automate</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {notifications.map((n) => (
                    <tr key={n.id} className={n.active ? '' : 'cat-row--inactive'}>
                      <td><strong>{n.display_name}</strong></td>
                      <td>{n.description || <span style={{ color: 'rgba(34,34,34,0.4)' }}>—</span>}</td>
                      <td>{n.flow_name || <span style={{ color: 'rgba(34,34,34,0.4)' }}>—</span>}</td>
                      <td>
                        <span className={`cat-badge ${n.active ? 'cat-badge--active' : 'cat-badge--inactive'}`}>
                          {n.active ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td>
                        <div className="cat-actions">
                          <button
                            className={`cat-action-btn ${n.active ? 'cat-action-delete' : 'cat-action-restore'}`}
                            onClick={() => setConfirmRow(n)}
                            title={n.active ? 'Desactivar notificación' : 'Activar notificación'}
                          >
                            {n.active ? <IconToggleOff /> : <IconToggleOn />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* ── Toggle confirmation ── */}
      {confirmRow && (
        <>
          <div className="cat-backdrop" onClick={() => setConfirmRow(null)} aria-hidden="true" />
          <div className="cat-confirm" role="alertdialog" aria-modal="true">
            <h3 className="cat-confirm-title">
              {confirmRow.active ? '¿Desactivar notificación?' : '¿Activar notificación?'}
            </h3>
            <p className="cat-confirm-msg">
              {confirmRow.active
                ? <>La notificación <strong>{confirmRow.display_name}</strong> dejará de enviarse. Podrás activarla nuevamente después.</>
                : <>La notificación <strong>{confirmRow.display_name}</strong> comenzará a enviarse cuando se dispare el evento correspondiente.</>
              }
            </p>
            <div className="cat-confirm-actions">
              <button
                className="cat-btn-cancel"
                onClick={() => setConfirmRow(null)}
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                className={confirmRow.active ? 'cat-btn-danger' : 'cat-btn-primary'}
                onClick={handleToggle}
                disabled={actionLoading}
              >
                {actionLoading
                  ? (confirmRow.active ? 'Desactivando…' : 'Activando…')
                  : (confirmRow.active ? 'Desactivar' : 'Activar')
                }
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Toast ── */}
      {toast && <SuccessToast message={toast} onDone={() => setToast('')} />}
    </div>
  );
}

export default GlobalNotifications;
